import React, { useEffect, useId, useRef, useState } from "react";
import { GROUP_ORDER, colorFor, labelFor } from "./palette";

// WHICH KINDS OF THING TO SHOW.
//
// Twenty kinds, and a reader almost never wants all of them at once: the
// person who came for the metals and the days does not want ninety-four plants
// and sixty-three Ptolemaic provinces in the way.
//
// A disclosure with checkboxes, not a `role="menu"`: a menu carries an
// arrow-key contract that would then have to be implemented, and these are
// independent choices that stay open while several are changed. Same shape as
// the chronology's layer panel, for the same reasons.
export default function KindPicker({ counts, onChange, selected }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const holder = useRef(null);

  const groups = GROUP_ORDER.filter((group) => counts[group]);
  const all = groups.length;
  const on = groups.filter((group) => selected.has(group));

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    const onDown = (event) => {
      if (!holder.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  const toggle = (group) => {
    const next = new Set(selected);
    if (next.has(group)) next.delete(group); else next.add(group);
    onChange(next);
  };

  return (
    <div className="kinds" ref={holder}>
      <button
        type="button"
        className="kinds__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid="atlas-kinds-toggle"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        Kinds to show
        {/* A dropdown that hides what is on is worse than the row it replaced. */}
        <span className="kinds__count" data-testid="atlas-kinds-count">
          {on.length === all ? "all" : `${on.length} of ${all}`}
        </span>
      </button>

      {on.length < all ? (
        <p className="kinds__active" data-testid="atlas-kinds-active">
          {on.map((group) => labelFor(group)).join(" · ")}
        </p>
      ) : null}

      {/* Kept mounted: these checkboxes are the reader's choice, and
          unmounting them on close would discard it. */}
      <div className="kinds__panel" id={panelId} hidden={!open}>
        <div className="kinds__bulk">
          <button
            type="button"
            data-testid="atlas-kinds-all"
            disabled={on.length === all}
            onClick={() => onChange(new Set(groups))}
          >
            Include every kind
          </button>
          <button
            type="button"
            data-testid="atlas-kinds-none"
            disabled={on.length === 0}
            onClick={() => onChange(new Set())}
          >
            Leave every kind out
          </button>
        </div>
        <ul className="kinds__list">
          {groups.map((group) => (
            <li key={group}>
              <label className="kinds__option">
                <input
                  type="checkbox"
                  checked={selected.has(group)}
                  data-testid={`atlas-kind-${group}`}
                  onChange={() => toggle(group)}
                />
                <span className="kinds__swatch" style={{ background: colorFor(group) }} aria-hidden="true" />
                <span>{labelFor(group)}</span>
                <span className="kinds__tally">{counts[group]}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
